// Google Analytics 4 helpers for the admin portal.
//
// Init: handled by the canonical inline snippet in `admin/index.html`
// (Google's documented setup — `window.gtag = function(){dataLayer.push(arguments)}`
// pattern with the loader async-injected from the same script). Doing it
// inline + at HTML parse time avoids the init-timing quirks the earlier
// dynamic-loader-from-JS approach was hitting (gtag.js loaded but
// silently refused to fire collect requests on dataLayer pushes).
//
// This module just exposes typed helpers that call the global `gtag`
// that the inline snippet defines. When `VITE_GA_MEASUREMENT_ID` is
// unset the inline snippet early-returns, so `window.gtag` is
// undefined here and every helper is a no-op.

const GA_ID = import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined
const ENABLED = typeof GA_ID === 'string' && GA_ID.length > 0

type GtagFn = (...args: unknown[]) => void

function callGtag(...args: unknown[]): void {
  const fn = (window as unknown as { gtag?: GtagFn }).gtag
  if (typeof fn === 'function') fn(...args)
}

/// Kept for back-compat with the previous public API — no-op now that
/// init happens inline in `admin/index.html`. Safe to call.
export function initAnalytics(): void {
  /* intentional no-op — see file-level comment */
}

/// Fire a `page_view` event. Called by the router-listener component
/// on every BrowserRouter location change so SPA navigations are
/// captured (default gtag config only fires once on initial load).
export function trackPageView(path: string, title?: string): void {
  if (!ENABLED) return
  callGtag('event', 'page_view', {
    page_path: path,
    page_title: title ?? document.title,
    page_location: window.location.href,
  })
}

/// Track an arbitrary admin action. Keep event names in snake_case
/// matching GA4 convention; params should be flat key/value pairs
/// (no nested objects — GA4 silently drops them).
///
/// Conventional events surfaced from admin mutations:
///   - admin_order_status_change   { to }
///   - admin_csv_export            { surface, has_filters }
///   - admin_order_void            { reason_code, channel }
///   - admin_order_refund          { method, amount_jod }
///   - admin_audit_log_filter      { admin, action, has_target }
///   - admin_settings_save         { tab }
export function trackEvent(name: string, params?: Record<string, string | number | boolean>): void {
  if (!ENABLED) return
  callGtag('event', name, params ?? {})
}
